import { 
  doc, 
  getDoc, 
  setDoc, 
  collection,
  getDocs,
  deleteDoc,
  query,
  orderBy
} from 'firebase/firestore';
import { db } from '../config/firebase';

const CARD_TEMPLATES_COLLECTION = 'cardTemplates';

/**
 * Initialize card templates from JSON data
 */
export const initializeCardTemplatesFromJson = async (jsonData) => {
  try {
    // Convert the JSON structure to proper templates
    const templates = [
      {
        id: 'welcome',
        type: 'welcome',
        title: jsonData.welcomeCard.title,
        description: jsonData.welcomeCard.description,
        estimatedTime: jsonData.welcomeCard.estimatedTime,
        icon: jsonData.welcomeCard.icon,
        order: 0,
        createdAt: new Date(),
        updatedAt: new Date()
      },
      ...jsonData.stages.map((stage, index) => ({
        id: stage.id,
        type: 'stage',
        title: stage.title,
        description: stage.description,
        additionalInfo: stage.additionalInfo || '',
        icon: stage.icon || {},
        order: index + 1,
        substeps: stage.substeps || [],
        createdAt: new Date(),
        updatedAt: new Date()
      }))
    ];

    // Add each template as a document in the collection
    for (const template of templates) {
      await setDoc(doc(db, CARD_TEMPLATES_COLLECTION, template.id), template);
    }

    return templates;
  } catch (error) {
    console.error('Error initializing card templates:', error);
    throw error;
  }
};

/**
 * Get all card templates
 */
export const getAllCardTemplates = async () => {
  try {
    const templatesQuery = query(
      collection(db, CARD_TEMPLATES_COLLECTION),
      orderBy('order', 'asc')
    );
    
    const templatesSnapshot = await getDocs(templatesQuery);
    
    if (templatesSnapshot.empty) {
      return [];
    }
    
    return templatesSnapshot.docs.map(doc => doc.data());
  } catch (error) {
    console.error('Error getting card templates:', error);
    throw error;
  }
};

/**
 * Get a card template by ID
 */
export const getCardTemplateById = async (templateId) => {
  try {
    const templateRef = doc(db, CARD_TEMPLATES_COLLECTION, templateId);
    const templateSnapshot = await getDoc(templateRef);
    
    if (!templateSnapshot.exists()) {
      return null;
    }
    
    return templateSnapshot.data();
  } catch (error) {
    console.error('Error getting card template:', error);
    throw error;
  }
};

/**
 * Create a new card template
 */
export const createCardTemplate = async (templateData) => {
  try {
    const timestamp = new Date();
    const newTemplate = {
      ...templateData,
      createdAt: timestamp,
      updatedAt: timestamp
    };
    
    await setDoc(doc(db, CARD_TEMPLATES_COLLECTION, templateData.id), newTemplate);
    
    return newTemplate;
  } catch (error) {
    console.error('Error creating card template:', error);
    throw error;
  }
};

/**
 * Update a card template
 */
export const updateCardTemplate = async (templateId, templateData) => {
  try {
    const templateRef = doc(db, CARD_TEMPLATES_COLLECTION, templateId);
    const templateSnapshot = await getDoc(templateRef);
    
    if (!templateSnapshot.exists()) {
      throw new Error(`Template with ID ${templateId} does not exist`);
    }
    
    const updatedTemplate = {
      ...templateData,
      updatedAt: new Date()
    };
    
    await setDoc(templateRef, updatedTemplate);
    
    return updatedTemplate;
  } catch (error) {
    console.error('Error updating card template:', error);
    throw error;
  }
};

/**
 * Delete a card template
 */
export const deleteCardTemplate = async (templateId) => {
  try {
    await deleteDoc(doc(db, CARD_TEMPLATES_COLLECTION, templateId));
    return true;
  } catch (error) {
    console.error('Error deleting card template:', error);
    throw error;
  }
};

/**
 * Get the number of card templates
 * Used to determine the order of new templates
 */
export const getCardTemplatesCount = async () => {
  try {
    const templatesSnapshot = await getDocs(collection(db, CARD_TEMPLATES_COLLECTION));
    return templatesSnapshot.size;
  } catch (error) {
    console.error('Error getting card templates count:', error);
    throw error;
  }
}; 